// French (fr-FR) presentation helpers for the ERP screens. Currency renders with a
// thin-space thousands separator and comma decimal — e.g. 1234.56 → "1 234,56 €".

const EUR_FMT = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Format a number (or numeric string) as French euros: "1 234,56 €". */
export function formatEUR(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  return EUR_FMT.format(Number.isFinite(v) ? v : 0);
}

/** Format an ISO timestamp as a French date "17/06/2026"; "—" when absent/invalid. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

const DATETIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Format an ISO timestamp as a French date + time in the VIEWER's local zone —
 * "2026-08-04T14:44:14.715Z" → "04/08/2026 16:44" in Paris. Intl resolves the offset, so this
 * is the counter's wall clock, not UTC; a raw ISO string on screen is unreadable at a counter.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  // fr-FR renders "04/08/2026 16:44"; older engines emit a comma, which reads oddly in a table.
  return Number.isNaN(d.getTime()) ? '—' : DATETIME_FMT.format(d).replace(',', '');
}

/**
 * A TecDoc technical criterion, as it is stored on `products.specifications` and returned by the
 * article endpoints. The unit is separate from the value, and — see `specLabel` — usually repeated
 * inside the label.
 */
export interface TechSpec {
  label: string;
  value: string;
  unit?: string | null;
}

/**
 * The criterion's label with a REDUNDANT trailing unit removed: "Épaisseur [mm]" → "Épaisseur".
 *
 * TecDoc names a dimensional criterion with its unit in brackets AND returns that unit separately,
 * so rendering both gives "Épaisseur [mm]: 16,9 mm" — the unit twice, once in a bracket that is
 * not part of the part's description. A supplier's own catalogue prints "Longueur: 272,0 mm", and
 * that is the line a counter reads.
 *
 * Only strips when the bracket genuinely holds THE SAME unit that will be printed beside the
 * value. A label whose bracket says something else ("Poids [kg]" against a unit of "g") keeps it,
 * and so does one with no unit to move it to — where the bracket is the only place the reader
 * would learn what the figure measures.
 */
export function specLabel(s: TechSpec): string {
  const unit = (s.unit ?? '').trim();
  if (!unit) return s.label;
  const m = /^(.*?)\s*\[([^\]]+)\]\s*$/.exec(s.label);
  if (!m) return s.label;
  return m[2].trim().toLowerCase() === unit.toLowerCase() ? m[1].trim() || s.label : s.label;
}

/** The criterion's value with its unit appended: "16,9" + "mm" → "16,9 mm". */
export function specValue(s: TechSpec): string {
  const unit = (s.unit ?? '').trim();
  return unit ? `${s.value} ${unit}` : s.value;
}
